import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger,
  Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Spinner
} from '@fluentui/react-components';
import { ArrowSync20Regular, Delete20Regular, Eye20Regular, MoreHorizontal20Regular, Search20Regular } from '@fluentui/react-icons';

import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import * as ComponentFunctions from './SettlementList.Functions';
import { groupByProtocol, inSelectedMonths, matchesSearch, SearchableSettlement, selectionLabel, statusCounts, statusMeta } from './SettlementList.Logic';
import { totalsByCurrency } from './SettlementList.Totals';
import { BatchPlan, canSettle, debtsLoaded, planBatch } from './SettlementList.Selection';
import PaymentFormComponent from '../../CashFlow/Payments/Payment.Form/Payment.Form.Component';
import { FormType as PaymentFormType } from '../../CashFlow/Payments/Payment.Form/Payment.Form.Function';
import MonthSelectorBar from '../MonthSelectorBar/MonthSelectorBar.Component';
import { countByMonth, MonthSelection, todaySelection } from '../MonthSelectorBar/MonthSelectorBar.Functions';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import './SettlementList.Styles.css';

type ViewMode = 'list' | 'protocol';

const fmtDate = (value: Date | string | null | undefined) => (value ? moment(value).format('DD/MM/YYYY') : '—');

/**
 * Liquidaciones por mes: elegís el mes en la grilla anual, filtrás por estado o
 * texto y trabajás sobre la tabla (ver, actualizar, eliminar).
 */
export const SettlementListComponent = (props: ComponentFunctions.Props) => {

  const navigate = useNavigate();
  const [t] = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedMonths, setSelectedMonths] = useState<MonthSelection[]>(() => [todaySelection()]);
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [view, setView] = useState<ViewMode>('list');
  const [toDelete, setToDelete] = useState<Settlement | null>(null);

  // ── Selección para cobrar varias en un solo movimiento ──
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [gathering, setGathering] = useState<boolean>(false);
  const [batchError, setBatchError] = useState<string | undefined>(undefined);
  const [batch, setBatch] = useState<BatchPlan | null>(null);

  const toggleSelected = (id: number) =>
    setSelectedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const selectedSettlements = (props.Settlements ?? []).filter((item) => selectedIds.includes(item.id));

  // Al pedir el cobro: se traen las deudas de las que falten y, con todas, se arma el plan.
  const startBatch = () => {
    setBatchError(undefined);
    selectedSettlements.filter((item) => !debtsLoaded(item)).forEach((item) => props.GetDebtsBySettlementId(item.id, undefined, false));
    setGathering(true);
  };

  useEffect(() => {
    if (!gathering) return;
    if (selectedSettlements.some((item) => !debtsLoaded(item) || item.isGettingDebts)) return;
    setGathering(false);
    const result = planBatch(selectedSettlements, props.OwnerId);
    if (result.error || !result.plan) {
      setBatchError(result.error);
      return;
    }
    setBatch(result.plan);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gathering, selectedSettlements.map((item) => `${item.id}:${debtsLoaded(item)}:${item.isGettingDebts ? 1 : 0}`).join(',')]);

  const closeBatch = () => {
    setBatch(null);
    setSelectedIds([]);
    // Las deudas cambiaron: se vuelven a pedir para que la lista refleje los saldos.
    selectedSettlements.forEach((item) => props.GetDebtsBySettlementId(item.id, undefined, false));
  };

  useEffect(() => {
    props.GetAllSettlements();
    props.GetAllMedicalProtocols();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contrapartes que todavía no están en el store se piden una vez.
  useEffect(() => {
    if (!(props.Settlements?.length)) return;
    const sponsors = new Set<number>();
    const practitioners = new Set<string>();
    const insurances = new Set<number>();
    props.Settlements.forEach((item) => {
      if ((item.sponsor?.length ?? 0) > 0) {
        const id = item.sponsor[0].sponsorId;
        if (!props.sponsorList?.find((sponsor) => sponsor.id === id)) sponsors.add(id);
      } else if ((item.practitioner?.length ?? 0) > 0 && (item.protocol?.length ?? 0) > 0) {
        const id = item.practitionerId;
        if (id && !props.practitionerList?.find((practitioner) => practitioner.userId === id)) practitioners.add(id);
      } else if ((item.agreements?.length ?? 0) > 0) {
        const id = item.medicalInsuranceId;
        if (id && !props.medicalInsuranceList?.find((insurance) => insurance.id === id)) insurances.add(id);
      }
    });
    sponsors.forEach((id) => props.GetSponsorById(id));
    practitioners.forEach((id) => props.GetPractitionerById(id));
    insurances.forEach((id) => props.GetMedicalInsuranceById(id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(props.Settlements ?? []).map((item) => item.id).join(',')]);

  // ── Nombres ──
  const protocolLabelOf = (item: Settlement): string | undefined => {
    const protocolId = item.protocol?.[0]?.protocolId;
    if (!protocolId) return undefined;
    const protocol = props.protocolList?.find((candidate) => candidate.id === protocolId);
    return protocol?.title || protocol?.protocolNumber || `Protocolo #${protocolId}`;
  };

  const entityOf = (item: Settlement): { label?: string; type?: 'sponsor' | 'practitioner' | 'agreement' } => {
    if ((item.sponsor?.length ?? 0) > 0) {
      const sponsor = props.sponsorList?.find((candidate) => candidate.id === item.sponsor[0].sponsorId);
      const name = sponsor ? (sponsor.person?.businessName || [sponsor.person?.firstName, sponsor.person?.lastName].filter(Boolean).join(' ')) : undefined;
      return { label: name ?? 'Sponsor', type: 'sponsor' };
    }
    if ((item.practitioner?.length ?? 0) > 0 && (item.protocol?.length ?? 0) > 0) {
      const practitioner = props.practitionerList?.find((candidate) => candidate.userId === item.practitionerId);
      return { label: practitioner?.fullName ?? 'Médico', type: 'practitioner' };
    }
    if ((item.agreements?.length ?? 0) > 0) {
      const insurance = props.medicalInsuranceList?.find((candidate) => candidate.id === item.medicalInsuranceId);
      return { label: insurance?.businessName ?? insurance?.title ?? 'Obra social', type: 'agreement' };
    }
    return {};
  };

  // ── Filtros ──
  const all = props.Settlements ?? [];
  const monthCounts = useMemo(() => countByMonth(all.map((item) => item.settlementDateRangeInit)), [all]);
  const inMonth = useMemo(() => all.filter((item) => inSelectedMonths(item, selectedMonths)), [all, selectedMonths]);

  const searchable: SearchableSettlement[] = useMemo(
    () => inMonth.map((settlement) => ({ settlement, protocolLabel: protocolLabelOf(settlement), entityLabel: entityOf(settlement).label })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [inMonth, props.protocolList, props.sponsorList, props.practitionerList, props.medicalInsuranceList]
  );

  const searched = searchable.filter((item) => matchesSearch(item, search));
  const counts = statusCounts(searched.map((item) => item.settlement));
  const visible = status === undefined ? searched : searched.filter((item) => item.settlement.status === status);
  const totals = totalsByCurrency(inMonth);

  const renderStatus = (item: Settlement) => {
    const meta = statusMeta(item.status);
    return <span className={`slt-tag slt-tag--${meta.tone}`}>{t(meta.key)}</span>;
  };

  const renderAmounts = (item: Settlement) => {
    const amounts = (item.totals ?? []).filter((total) => (total.ownerAmountWithholdingTotal ?? 0) !== 0);
    if (amounts.length === 0) return <span className="slt-muted">—</span>;
    return amounts.map((total, index) => (
      <span key={`${total.currencyCode}-${index}`} className="slt-amount slt-num">
        {formatCurrencyAmountWithIssue(total.ownerAmountWithholdingTotal, total.currencyCode)}
      </span>
    ));
  };

  const renderRow = (item: Settlement, protocolLabel?: string) => {
    const entity = entityOf(item);
    return (
      <tr key={item.id} className={`slt-row${selectedIds.includes(item.id) ? ' is-selected' : ''}`} onClick={() => navigate(`/settlements/${item.id}`)}>
        <td className="slt-check" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedIds.includes(item.id)}
            disabled={!canSettle(item)}
            title={canSettle(item) ? 'Seleccionar para cobrar' : 'Sólo se cobran las confirmadas o con deuda impaga'}
            aria-label={`Seleccionar ${item.name}`}
            onChange={() => toggleSelected(item.id)}
          />
        </td>
        <td>
          <div className="slt-cell-main">
            {protocolLabel && <span className="slt-protocol">{protocolLabel}</span>}
            <span className="slt-name">{item.name}</span>
            {item.identifier && <span className="slt-id">#{item.identifier}</span>}
          </div>
        </td>
        <td>
          {entity.label
            ? <span className={`slt-entity slt-entity--${entity.type}`} title={entity.label}>{entity.label}</span>
            : <span className="slt-muted">—</span>}
        </td>
        <td className="slt-num slt-muted" title={item.settlementDateRangeEnd ? `Período ${fmtDate(item.settlementDateRangeInit)} – ${fmtDate(item.settlementDateRangeEnd)}` : undefined}>
          {fmtDate(item.settlementDateRangeInit)}
        </td>
        <td>{item.voucheredNumber ? <span className="slt-num">{item.voucheredNumber}</span> : <span className="slt-noinvoice">Sin factura</span>}</td>
        <td>{renderStatus(item)}</td>
        <td className="slt-r"><div className="slt-amounts">{renderAmounts(item)}</div></td>
        <td className="slt-r" onClick={(event) => event.stopPropagation()}>
          {item.isRedreshing ? <Spinner size="extra-tiny" /> : (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <button type="button" className="slt-more" aria-label="Acciones"><MoreHorizontal20Regular /></button>
              </MenuTrigger>
              <MenuPopover className="slt-menu">
                <MenuList>
                  <MenuItem icon={<Eye20Regular />} onClick={() => navigate(`/settlements/${item.id}`)}>Ver detalle</MenuItem>
                  <MenuItem icon={<ArrowSync20Regular />} onClick={() => props.RefreshSettlementById(item.id)}>Actualizar</MenuItem>
                  <MenuItem icon={<Delete20Regular />} className="slt-menu__danger" onClick={() => setToDelete(item)}>Eliminar</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </td>
      </tr>
    );
  };

  const renderTable = () => {
    if (props.isLoading && all.length === 0) {
      return <div className="slt-state"><Spinner size="extra-tiny" /> {t('settlementListComponent.loading.loadingSettlements')}</div>;
    }
    if (visible.length === 0) {
      return (
        <div className="slt-empty">
          <span className="slt-empty__title">{inMonth.length === 0 ? 'No hay liquidaciones en este período' : 'Nada coincide con el filtro'}</span>
          <span>{inMonth.length === 0 ? 'Elegí otro mes en la grilla de arriba.' : 'Probá con otra búsqueda o quitá el estado.'}</span>
        </div>
      );
    }

    const head = (
      <thead>
        <tr>
          <th className="slt-check" />
          <th>Liquidación</th>
          <th>Contraparte</th>
          <th>Fecha</th>
          <th>Factura</th>
          <th>Estado</th>
          <th className="slt-r">Monto</th>
          <th />
        </tr>
      </thead>
    );

    if (view === 'protocol') {
      const groups = groupByProtocol(visible);
      return (
        <div className="slt-table-wrap">
          <table className="slt-table">
            {head}
            {groups.map((group) => (
              <tbody key={group.key}>
                <tr className="slt-group">
                  <td colSpan={6}>{group.label} <span className="slt-group__count">{group.items.length}</span></td>
                  <td className="slt-r slt-num">{totalsByCurrency(group.items).map((total) => formatCurrencyAmountWithIssue(total.amount, total.currency)).join(' · ')}</td>
                  <td />
                </tr>
                {group.items.map((item) => renderRow(item))}
              </tbody>
            ))}
          </table>
        </div>
      );
    }

    return (
      <div className="slt-table-wrap">
        <table className="slt-table">
          {head}
          <tbody>{visible.map((item) => renderRow(item.settlement, item.protocolLabel))}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="slt-root">
      <MonthSelectorBar selected={selectedMonths} onChange={(months) => { setSelectedMonths(months); setStatus(undefined); }} counts={monthCounts} />

      {props.failOnLoading && (
        <div className="slt-banner"><strong>{t('settlementListComponent.errorMessage.message')}</strong><span>{props.error?.ErrorMessage}</span></div>
      )}

      {/* ── Mes elegido: cuántas y cuánto ── */}
      <div className="slt-head">
        <div className="slt-head__left">
          <h2 className="slt-title">{selectionLabel(selectedMonths)}</h2>
          <div className="slt-figures">
            <span className="slt-figures__count">{inMonth.length} {inMonth.length === 1 ? 'liquidación' : 'liquidaciones'}</span>
            {totals.map((total) => (
              <span key={total.currency} className="slt-figures__total slt-num">{formatCurrencyAmountWithIssue(total.amount, total.currency)}</span>
            ))}
          </div>
        </div>
        <div className="slt-head__right">
          <label className="slt-search">
            <Search20Regular />
            <input
              type="search"
              placeholder="Protocolo, contraparte o factura…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('settlementListComponent.searchBox.search')}
            />
          </label>
          <div className="slt-segmented" role="tablist" aria-label="Vista">
            <button type="button" role="tab" aria-selected={view === 'list'} className={`slt-segmented__btn${view === 'list' ? ' is-active' : ''}`} onClick={() => setView('list')}>Lista</button>
            <button type="button" role="tab" aria-selected={view === 'protocol'} className={`slt-segmented__btn${view === 'protocol' ? ' is-active' : ''}`} onClick={() => setView('protocol')}>Por protocolo</button>
          </div>
        </div>
      </div>

      {/* ── Estados ── */}
      {searched.length > 0 && (
        <div className="slt-pills">
          <button type="button" className={`slt-pill${status === undefined ? ' is-active' : ''}`} onClick={() => setStatus(undefined)}>
            Todos <span className="slt-pill__count">{searched.length}</span>
          </button>
          {counts.map((item) => (
            <button key={item.status} type="button" className={`slt-pill${status === item.status ? ' is-active' : ''}`} onClick={() => setStatus(status === item.status ? undefined : item.status)}>
              <span className={`slt-dot slt-dot--${statusMeta(item.status).tone}`} aria-hidden /> {t(statusMeta(item.status).key)} <span className="slt-pill__count">{item.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Barra de selección: cobrar varias de una vez ── */}
      {selectedIds.length > 0 && (
        <div className="slt-selection" role="region" aria-label="Liquidaciones seleccionadas">
          <span className="slt-selection__count">{selectedIds.length} {selectedIds.length === 1 ? 'seleccionada' : 'seleccionadas'}</span>
          <span className="slt-selection__total slt-num">
            {totalsByCurrency(selectedSettlements).map((total) => formatCurrencyAmountWithIssue(total.amount, total.currency)).join(' · ')}
          </span>
          {batchError && <span className="slt-selection__error" role="alert">{batchError}</span>}
          <span className="slt-selection__spacer" />
          <button type="button" className="slt-btn" onClick={() => { setSelectedIds([]); setBatchError(undefined); }}>Quitar selección</button>
          <button type="button" className="slt-btn slt-btn--primary" onClick={startBatch} disabled={gathering}>
            {gathering ? <><Spinner size="extra-tiny" /> Buscando deudas…</> : 'Registrar cobro'}
          </button>
        </div>
      )}

      <div className="slt-card">{renderTable()}</div>

      <PaymentFormComponent
        FormType={batch?.direction === 'payer' ? PaymentFormType.payer : PaymentFormType.receiver}
        ReferalId={props.OwnerId}
        FixedAssociatedEntityIdentifier={undefined}
        PrefillDebtsData={batch?.debts}
        isOpen={batch !== null}
        onClose={closeBatch}
      />

      <Dialog open={toDelete !== null} onOpenChange={(_e, data) => { if (!data.open) setToDelete(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('settlementListComponent.actions.delete.title')}</DialogTitle>
            <DialogContent>
              <strong>{toDelete?.name}</strong>
              <div>{t('settlementListComponent.actions.delete.messageOne')}</div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('settlementListComponent.actions.delete.buttonCancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                disabled={props.isDeleting}
                icon={props.isDeleting ? <Spinner size="extra-tiny" /> : <Delete20Regular />}
                onClick={() => { if (toDelete) props.DeleteSettlementById(toDelete.id); setToDelete(null); }}
              >
                {t('settlementListComponent.actions.delete.buttonConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(SettlementListComponent as any);
